import { useState, useMemo } from 'react'
import { useDebounce } from '../hooks/useDebounce'

interface SearchableData {
  [key: string]: any
}

interface UseSearchFilterOptions {
  searchFields: string[]
  debounceMs?: number
}

export const useSearchFilter = <T extends SearchableData>(
  data: T[],
  options: UseSearchFilterOptions
) => {
  const { searchFields, debounceMs = 300 } = options
  const [searchText, setSearchText] = useState('')
  
  const debouncedSearchText = useDebounce(searchText, debounceMs)

  const stripHtmlTags = (html: string): string => {
    if (!html) return ''
    return html.replace(/<[^>]*>/g, '').trim()
  }

  const getNestedValue = (obj: any, path: string): string => {
    const keys = path.split('.')
    let value = obj
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key]
      } else {
        return ''
      }
    }
    
    return String(value || '')
  }

  const filteredData = useMemo(() => {
    if (!debouncedSearchText.trim()) {
      return data
    }

    const searchLower = debouncedSearchText.toLowerCase().trim()

    return data.filter((item) => {
      return searchFields.some((field) => {
        let fieldValue = getNestedValue(item, field)
        
        if (fieldValue.includes('<') && fieldValue.includes('>')) {
          fieldValue = stripHtmlTags(fieldValue)
        }
        
        return fieldValue.toLowerCase().includes(searchLower)
      })
    })
  }, [data, debouncedSearchText, searchFields])

  const handleSearchChange = (value: string) => {
    setSearchText(value)
  }

  const clearSearch = () => {
    setSearchText('')
  }

  return {
    searchText,
    filteredData,
    handleSearchChange,
    clearSearch,
    isSearching: searchText.length > 0
  }
} 